import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  GetCurrentUser,
  GetCompanyInfo,
  GetEmailNotification,
  GetSearchConditionList,
  GetSearchUserListByGroupIdAndName,
  GetSettingGroupList,
  InviteUser,
  UpdateEmailNotification,
  GetRoleList,
} from "@/services/Setting";

import { ProjectType } from "@/consts/Bid";

import { isNotEmpty } from "@/libs/utils";

import { TabMenu } from "@/types/Shared/tab";
import {
  CompanyInfo,
  CurrentUserContent,
  EmailNotificationContent,
  InviteUserResponse,
  Role,
  SearchUser,
  SettingGroup,
  UpdateEmailNotificationRequest,
} from "@/types/setting";
import { ValueType } from "@/types/bid";

import { AdminRoleName, DefaultSearchUser } from "@/consts/Setting";

import { useIsProductManager } from "@/hooks/useIsFinancialManager";

import { showToast } from "@/utils/toastUtils";

import useUserStore from "@/stores/User";

import ContentLayout from "@/layouts/ContentLayout";

import CommonTab from "@/components/Common/Tab";
import DetailCardWrapper from "@/components/DetailCardWrapper";
import DetailCard from "@/components/DetailCard";
import DetailCardItemWrapper from "@/components/DetailCardItemWrapper";
import DetailCardItem from "@/components/DetailCardItem";
import CommonCheckbox from "@/components/Common/Checkbox";
import CommonSelect from "@/components/Common/Select";
import CommonInput from "@/components/Common/Input";
import CommonImg from "@/components/Common/Img";
import ItemBadge from "@/components/ItemBadge";
import CommonPagination from "@/components/Common/Pagination";
import UserCard from "@/components/UserCard";
import DialogMemberDepartment from "@/components/DialogUserDepartment";
import SubstituteProductList from "@/components/SubstituteProductList";

import ProjectInfoIcon from "@/assets/images/BidDetail/project-info-icon.png";
import LicenseIcon from "@/assets/images/Setting/license-icon.png";
import SupplyProductIcon from "@/assets/images/Setting/supply-product-icon.png";
import ManufactureProductIcon from "@/assets/images/Setting/manufacture-product-icon.png";
import EmailIcon from "@/assets/images/Setting/email-icon.png";
import TeamIcon from "@/assets/images/Setting/team-icon.png";
import PlusIcon from "@/assets/images/plug-button-icon.png";
import SearchIcon from "@/assets/images/search-icon.svg";

export default function Setting() {
  const location = useLocation();
  const isProductManager = useIsProductManager();
  // 대체품 목록 탭 (모든 사용자 접근 가능, 추가/삭제/수정은 PM만)
  const isSubstituteTab = location.pathname === "/setting/substitute";

  const tabData: TabMenu[] = [
    {
      path: "/setting",
      label: "주식회사 써모피셔 사이언티픽",
    },
    // 모든 사용자에게 대체품 목록 탭 표시
    {
      path: "/setting/substitute",
      label: "대체품 목록",
    },
  ];

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>();
  const [emailNoti, setEmailNoti] = useState<EmailNotificationContent>(
    {} as EmailNotificationContent
  );
  const [searchConditionList, setSearchConditionList] = useState<ValueType[]>(
    [] as ValueType[]
  );
  const [userList, setUserList] = useState<CurrentUserContent[]>([]);
  const [totalUser, setTotalUser] = useState<number>(0);
  const [groupList, setGroupList] = useState<SettingGroup[]>([]);
  const [inviteUser, setInviteUser] = useState<InviteUserResponse>(
    {} as InviteUserResponse
  );

  const [searchUser, setSearchUser] = useState<SearchUser>(DefaultSearchUser);

  const [roleList, setRoleList] = useState<Role[]>([]);

  const storedUser = useUserStore((state) => state.user);
  const setStoredUser = useUserStore((state) => state.setUser);

  const getCompanyInfo = async () => {
    try {
      const result = await GetCompanyInfo();
      setCompanyInfo(result.data.pods[0].content);
    } catch (e) {
    } finally {
    }
  };
  const getEmailNotification = async () => {
    try {
      const result = await GetEmailNotification();
      setEmailNoti(result.data.pods[0].content);
    } catch (e) {
    } finally {
    }
  };
  const getSearchConditionList = async () => {
    try {
      const result = await GetSearchConditionList();
      setSearchConditionList(
        [{ label: "", value: "" }].concat(
          result.data.pods[0].content.items.map((item) => ({
            value: item.search_id,
            label: item.search_name,
          }))
        )
      );
    } catch (e) {
    } finally {
    }
  };
  const getGroupList = async () => {
    try {
      const result = await GetSettingGroupList();
      const content = result.data.pods[0].content;
      setGroupList(content);
      setInviteUser((prev) => ({ ...prev, group_id: content[0].group_id }));
    } catch (e) {
    } finally {
    }
  };

  const getEmailNotificationData = (
    emailNotiData: UpdateEmailNotificationRequest
  ) => {
    return (
      `project_type="${emailNotiData.project_type}", ` +
      `search_id1=${emailNotiData.search_id1 === null ? "None" : `"${emailNotiData.search_id1}"`}, ` +
      `search_id2=${emailNotiData.search_id2 === null ? "None" : `"${emailNotiData.search_id2}"`}, ` +
      `subscribed=${emailNotiData.subscribed ? "True" : "False"}`
    );
  };

  const updateEmailNotification = async (
    emailNotiData: UpdateEmailNotificationRequest
  ) => {
    try {
      const params = getEmailNotificationData(emailNotiData);
      const result = await UpdateEmailNotification(params);
      if (result.data.success) {
        getEmailNotification();
      }
    } catch (e) {
    } finally {
    }
  };

  const getGroupListToValueType = () =>
    groupList?.map((group) => ({
      label: group.group_name,
      value: group.group_id,
    }));

  const isInvalidInviteUser = () => {
    return !(
      isNotEmpty(inviteUser.group_id) &&
      isNotEmpty(inviteUser.email) &&
      isNotEmpty(inviteUser.last_name) &&
      isNotEmpty(inviteUser.first_name)
    );
  };
  const handleClickInviteUser = async () => {
    if (isInvalidInviteUser()) {
      showToast({
        type: "error",
        message: "초대할 사용자 정보를 모두 입력해주세요",
      });
      return;
    }
    try {
      const params = `group_id="${inviteUser.group_id}", email="${inviteUser.email}", first_name="${inviteUser.first_name}", last_name="${inviteUser.last_name}"`;
      const result = await InviteUser(params);
      if (result.data.success) {
        setInviteUser({} as InviteUserResponse);
        setSearchUser(DefaultSearchUser);
        handleSearchUser(DefaultSearchUser);
      }
    } catch (e) {
    } finally {
    }
  };

  const handleSearchUser = async (newSearch?: SearchUser) => {
    if (!newSearch) {
      setSearchUser((prev) => ({ ...prev, page_no: 1 }));
    }
    const userSearchData = newSearch ?? { ...searchUser, page_no: 1 };
    try {
      const params =
        `page_no=${userSearchData.page_no}, ` +
        `page_size=${userSearchData.page_size}, ` +
        `group_id=${userSearchData.group_id === "" || userSearchData.group_id === "ALL" ? "None" : `"${userSearchData.group_id}"`}, ` +
        `name="${userSearchData.name}"`;
      const result = await GetSearchUserListByGroupIdAndName(params);
      if (result.data.success) {
        setUserList(result.data.pods[0].content.items);
        setTotalUser(result.data.pods[0].content.total_count);
      }
    } catch (e) {
    } finally {
    }
  };

  const getCurrentUser = async () => {
    try {
      const result = await GetCurrentUser();
      if (result.data.success) {
        setStoredUser(result.data.pods[0].content);
      }
    } catch (e) {
    } finally {
    }
  };

  const getRoleList = async () => {
    try {
      const result = await GetRoleList();
      setRoleList(result.data.pods[0].content);
    } catch (e) {
    } finally {
    }
  };

  const hasAdminRole = useMemo(() => {
    return !!storedUser?.roles?.find(
      (role: Role) => role.name === AdminRoleName
    );
  }, [storedUser]);

  const onLoad = () => {
    getCompanyInfo();
    getEmailNotification();
    getSearchConditionList();
    handleSearchUser();
    getGroupList();
    getCurrentUser();
    getRoleList();
  };

  useEffect(() => {
    onLoad();
  }, []);

  return (
    <ContentLayout>
      <CommonTab variants="string" data={tabData} />
      {isSubstituteTab ? (
        <SubstituteProductList />
      ) : (
      <DetailCardWrapper>
        <DetailCard
          icon={ProjectInfoIcon}
          iconAlt="Company Info"
          title="기업 정보"
          cardClassName="col-span-6"
          childrenClassName="h-fit"
        >
          <DetailCardItemWrapper>
            <DetailCardItem label="대표자" value={companyInfo?.ceo_name} />
            <DetailCardItem label="주소" value={companyInfo?.company_address} />
            <DetailCardItem
              label="사업자 번호"
              value={companyInfo?.business_no}
            />
            <DetailCardItem label="설립일" value={companyInfo?.founding_dt} />
          </DetailCardItemWrapper>
        </DetailCard>
        <DetailCard icon={LicenseIcon} iconAlt="License" title="보유 업종 코드">
          <div className="flex flex-col gap-[10px]">
            {companyInfo?.licenses.map((license, licenseIndex) => (
              <div key={licenseIndex} className="flex flex-col gap-[10px]">
                <ItemBadge
                  value={`${license.license_name} - ${license.license_id}`}
                />
                {licenseIndex < companyInfo?.licenses.length - 1 && <hr />}
              </div>
            ))}
          </div>
        </DetailCard>
        <DetailCard
          icon={SupplyProductIcon}
          iconAlt="Supply Product"
          title="보유 공급 물품"
        >
          <div className="flex flex-col gap-[10px]">
            {companyInfo?.supply_products.map((supply, supplyIndex) => (
              <div key={supplyIndex} className="flex flex-col gap-[10px]">
                <ItemBadge
                  value={`${supply.product_name} - ${supply.product_id}`}
                />
                {supplyIndex < companyInfo?.supply_products.length - 1 && (
                  <hr />
                )}
              </div>
            ))}
          </div>
        </DetailCard>
        <DetailCard
          icon={ManufactureProductIcon}
          iconAlt="Manufacture Product"
          title="보유 제조 물품"
        >
          <div className="flex flex-col gap-[10px]">
            {companyInfo?.manufactured_products.map((manu, manuIndex) => (
              <div key={manuIndex} className="flex flex-col gap-[10px]">
                <ItemBadge
                  value={`${manu.product_name} - ${manu.product_id}`}
                />
                {manuIndex < companyInfo?.manufactured_products.length - 1 && (
                  <hr />
                )}
              </div>
            ))}
          </div>
        </DetailCard>
        <DetailCard
          icon={EmailIcon}
          iconAlt="Recommend Bid Emailing"
          title="추천 입찰 공고 이메일링"
          cardClassName="col-span-3"
          childrenClassName="h-fit"
          titleChildren={
            <CommonCheckbox
              label="입찰 공고 추천 받지 않기"
              defaultValue={emailNoti?.subscribed_original}
              onChange={(value) => {
                const params = {
                  project_type: ProjectType.Original,
                  search_id1: emailNoti?.original_item1?.search_id ?? null,
                  search_id2: emailNoti?.original_item2?.search_id ?? null,
                  subscribed: value,
                };
                updateEmailNotification(params);
              }}
            />
          }
        >
          <div className="flex flex-col gap-[20px]">
            <div className="flex flex-col gap-[12px]">
              <div className="font-semibold">받을 검색 조건 1</div>
              <CommonSelect
                useAll={false}
                defaultValue={emailNoti?.original_item1?.search_id ?? ""}
                optionList={searchConditionList.filter(
                  (item) => item.value !== emailNoti?.original_item2?.search_id
                )}
                onChange={(value) => {
                  const params = {
                    project_type: ProjectType.Original,
                    search_id1: value === "" ? null : value,
                    search_id2: emailNoti?.original_item2?.search_id ?? null,
                    subscribed: emailNoti.subscribed_original,
                  };
                  updateEmailNotification(params);
                }}
              />
            </div>
            <div className="flex flex-col gap-[12px]">
              <div className="font-semibold">받을 검색 조건 2</div>
              <CommonSelect
                useAll={false}
                defaultValue={emailNoti?.original_item2?.search_id ?? ""}
                optionList={searchConditionList.filter(
                  (item) => item.value !== emailNoti?.original_item1?.search_id
                )}
                onChange={(value) => {
                  const params = {
                    project_type: ProjectType.Original,
                    search_id1: emailNoti?.original_item1?.search_id ?? null,
                    search_id2: value === "" ? null : value,
                    subscribed: emailNoti.subscribed_original,
                  };
                  updateEmailNotification(params);
                }}
              />
            </div>
          </div>
        </DetailCard>
        <DetailCard
          icon={EmailIcon}
          iconAlt="Recommend Specification Emailing"
          title="추천 사전 규격 이메일링"
          cardClassName="col-span-3"
          childrenClassName="h-fit"
          titleChildren={
            <CommonCheckbox
              label="사전 규격 추천 받지 않기"
              defaultValue={emailNoti?.subscribed_procure}
              onChange={(value) => {
                const params = {
                  project_type: ProjectType.Procure,
                  search_id1: emailNoti?.procure_item1?.search_id ?? null,
                  search_id2: emailNoti?.procure_item2?.search_id ?? null,
                  subscribed: value,
                };
                updateEmailNotification(params);
              }}
            />
          }
        >
          <div className="flex flex-col gap-[20px]">
            <div className="flex flex-col gap-[12px]">
              <div className="font-semibold">받을 검색 조건 1</div>
              <CommonSelect
                useAll={false}
                defaultValue={emailNoti?.procure_item1?.search_id ?? ""}
                optionList={searchConditionList.filter(
                  (item) => item.value !== emailNoti?.procure_item2?.search_id
                )}
                onChange={(value) => {
                  const params = {
                    project_type: ProjectType.Procure,
                    search_id1: value === "" ? null : value,
                    search_id2: emailNoti?.procure_item2?.search_id ?? null,
                    subscribed: emailNoti.subscribed_procure,
                  };
                  updateEmailNotification(params);
                }}
              />
            </div>
            <div className="flex flex-col gap-[12px]">
              <div className="font-semibold">받을 검색 조건 2</div>
              <CommonSelect
                useAll={false}
                defaultValue={emailNoti?.procure_item2?.search_id ?? ""}
                optionList={searchConditionList.filter(
                  (item) => item.value !== emailNoti?.procure_item1?.search_id
                )}
                onChange={(value) => {
                  const params = {
                    project_type: ProjectType.Procure,
                    search_id1: emailNoti?.procure_item1?.search_id ?? null,
                    search_id2: value === "" ? null : value,
                    subscribed: emailNoti.subscribed_procure,
                  };
                  updateEmailNotification(params);
                }}
              />
            </div>
          </div>
        </DetailCard>
        <DetailCard
          icon={TeamIcon}
          iconAlt="Team Member Setting"
          cardClassName="col-span-6"
          title="팀 멤버 설정"
          titleClassName="flex justify-between"
          childrenClassName="h-fit"
          titleChildren={
            hasAdminRole && (
              <div className="flex gap-[8px]">
                <CommonInput
                  defaultValue={inviteUser?.email}
                  placeholder="초대할 이메일을 입력하세요"
                  onChange={(value) => {
                    setInviteUser((prev) => ({ ...prev, email: value }));
                  }}
                />
                <CommonInput
                  defaultValue={inviteUser?.last_name}
                  placeholder="성을 입력하세요"
                  onChange={(value) => {
                    setInviteUser((prev) => ({ ...prev, last_name: value }));
                  }}
                />
                <CommonInput
                  defaultValue={inviteUser?.first_name}
                  placeholder="이름을 입력해주세요"
                  onChange={(value) => {
                    setInviteUser((prev) => ({ ...prev, first_name: value }));
                  }}
                />
                <CommonSelect
                  useAll={false}
                  defaultValue={inviteUser?.group_id ?? ""}
                  optionList={getGroupListToValueType()}
                  onChange={(value) => {
                    setInviteUser((prev) => ({ ...prev, group_id: value }));
                  }}
                />
                <CommonImg
                  src={PlusIcon}
                  alt="Add Member"
                  className="size-[28px] cursor-pointer"
                  onClick={handleClickInviteUser}
                />
              </div>
            )
          }
        >
          <div className="flex flex-col gap-[10px]">
            <div className="flex gap-[10px] items-center">
              {hasAdminRole && (
                <DialogMemberDepartment
                  onReload={() => {
                    // getUserList();
                    handleSearchUser();
                    getGroupList();
                  }}
                />
              )}
              <CommonSelect
                defaultValue={searchUser.group_id}
                optionList={getGroupListToValueType()}
                onChange={(value) => {
                  setSearchUser((prev) => ({ ...prev, group_id: value }));
                }}
              />
              <CommonInput
                defaultValue={searchUser.name}
                placeholder="이름을 검색하세요"
                onChange={(value) => {
                  setSearchUser((prev) => ({ ...prev, name: value }));
                }}
                onKeyDownEnter={(value) => {
                  setSearchUser((prev) => ({ ...prev, name: value }));
                  handleSearchUser();
                }}
              />
              <div className="size-[28px] bg-dark-brown rounded-[4px] flex justify-center items-center">
                <CommonImg
                  src={SearchIcon}
                  alt="Search"
                  className="size-[18px] cursor-pointer"
                  onClick={handleSearchUser}
                />
              </div>
            </div>
            <div className="flex flex-col gap-[10px]">
              <div className="w-full overflow-x-auto">
                <div className="min-w-[1100px] grid grid-cols-4 gap-y-2.5 gap-x-2.5">
                  {userList?.map((user, userIndex) => (
                    <UserCard
                      hasAdminRole={hasAdminRole}
                      currentUser={storedUser}
                      key={userIndex}
                      user={user}
                      userIndex={userIndex}
                      setUserList={setUserList}
                      groupList={groupList}
                      roleList={roleList}
                      onRefreshUserList={() => {
                        handleSearchUser(searchUser);
                      }}
                      onRefreshCurrentUser={getCurrentUser}
                    />
                  ))}
                </div>
              </div>
              <CommonPagination
                totalItems={totalUser}
                itemsPerPage={searchUser.page_size}
                currentPage={searchUser.page_no}
                onPageChange={(value) => {
                  setSearchUser((prev) => {
                    const newData = { ...prev, page_no: value };
                    handleSearchUser(newData);
                    return newData;
                  });
                }}
              />
            </div>
          </div>
        </DetailCard>
      </DetailCardWrapper>
      )}
    </ContentLayout>
  );
}
